/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { animate, eases, JSAnimation } from "animejs"
import React, {
    FC,
    MutableRefObject,
    PropsWithChildren,
    useEffect,
    useRef,
    useState,
} from "react"

export const CommonTransition: FC<
    PropsWithChildren<{
        state: unknown
        nodeRef: MutableRefObject<HTMLDivElement | null>
        duration?: number
        translateDir?: "x" | "y" | "-x" | "-y"
        offset?: number
    }>
> = ({
    state,
    nodeRef,
    duration = 300,
    translateDir,
    offset = 1,
    children,
}) => {
    const firstEnter = useRef(true)

    type STATUS = "IDLE" | "LEAVING" | "ENTERING"
    const [status, setStatus] = useState<STATUS>("IDLE")
    const nextState = useRef(state)
    // 内部控制的state
    const [innerState, setInnerState] = useState(state)
    // 内部渲染的节点
    const [innerChildren, setInnerChildren] = useState(children)
    useEffect(() => {
        setInnerChildren(children)
    }, [innerState])
    useEffect(() => {
        if (state === innerState) {
            setInnerChildren(children)
        }
    }, [children])
    useEffect(() => {
        if (firstEnter.current) {
            firstEnter.current = false
            return
        }
        nextState.current = state
        if (status !== "LEAVING") {
            setStatus("LEAVING")
        }
    }, [state])
    useEffect(() => {
        const node = nodeRef.current
        let animeInst: null | JSAnimation = null
        if (status === "LEAVING") {
            const control = { progress: 0 }
            animeInst = animate(control, {
                progress: [0, 1],
                duration: duration,
                ease: eases.outQuad,
                onUpdate() {
                    if (node) {
                        node.style.opacity = `${1 - control.progress}`
                        if (translateDir === "x") {
                            node.style.transform = `translateX(${
                                -control.progress * 2 * offset
                            }rem)`
                        } else if (translateDir === "-x") {
                            node.style.transform = `translateX(${
                                -control.progress * -2 * offset
                            }rem)`
                        } else if (translateDir === "y") {
                            node.style.transform = `translateY(${
                                -control.progress * 2 * offset
                            }rem)`
                        } else if (translateDir === "-y") {
                            node.style.transform = `translateY(${
                                -control.progress * -2 * offset
                            }rem)`
                        }
                    }
                },
                onComplete() {
                    setInnerState(nextState.current)
                    setStatus("ENTERING")
                },
            })
        } else if (status === "ENTERING") {
            const control = { progress: 0 }
            animeInst = animate(control, {
                progress: [0, 1],
                duration: duration,
                ease: eases.outQuad,
                onUpdate() {
                    if (node) {
                        node.style.opacity = `${control.progress}`
                        if (translateDir === "x") {
                            node.style.transform = `translateX(${
                                (1 - control.progress) * 2 * offset
                            }rem)`
                        } else if (translateDir === "-x") {
                            node.style.transform = `translateX(${
                                (1 - control.progress) * -2 * offset
                            }rem)`
                        } else if (translateDir === "y") {
                            node.style.transform = `translateY(${
                                (1 - control.progress) * 2 * offset
                            }rem)`
                        } else if (translateDir === "-y") {
                            node.style.transform = `translateY(${
                                (1 - control.progress) * -2 * offset
                            }rem)`
                        }
                    }
                },
                onComplete() {
                    if (node) {
                        node.style.opacity = ""
                        if (translateDir) {
                            node.style.transform = ""
                        }
                    }
                    setStatus("IDLE")
                },
            })
        } else if (status === "IDLE") {
            if (nextState.current !== innerState) {
                setStatus("LEAVING")
            }
        }
        return () => {
            animeInst?.pause()
        }
    }, [status])
    return <>{innerChildren}</>
}
